interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  dispose: () => void;
}

const BROWSER_SAFE_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp'
]);

async function decodeWithImageElement(file: File): Promise<DecodedImage> {
  const objectUrl = URL.createObjectURL(file);

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => {
      if (!image.naturalWidth || !image.naturalHeight) {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('图片内容无法识别'));
        return;
      }

      resolve({
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        dispose: () => URL.revokeObjectURL(objectUrl)
      });
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('图片内容无法识别'));
    };
    image.src = objectUrl;
  });
}

async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      if (bitmap.width && bitmap.height) {
        return {
          source: bitmap,
          width: bitmap.width,
          height: bitmap.height,
          dispose: () => bitmap.close()
        };
      }
      bitmap.close();
    } catch {
      // Older iOS/Android WebViews may expose createImageBitmap but fail for camera files.
    }
  }

  return decodeWithImageElement(file);
}

function canKeepOriginal(file: File) {
  return BROWSER_SAFE_IMAGE_TYPES.has(file.type.toLowerCase())
    && /\.(?:jpe?g|png|gif|webp)$/i.test(file.name);
}

async function convertHeicToJpeg(file: File, quality: number): Promise<File> {
  // HEIC 解码器体积较大，只在浏览器原生无法读取相机图片时按需加载。
  const { heicTo, isHeic } = await import('heic-to/csp');
  if (!(await isHeic(file))) {
    throw new Error('图片内容无法识别，请选择完整的 JPG、PNG、WebP 或 HEIC 图片');
  }

  const blob = await heicTo({
    blob: file,
    type: 'image/jpeg',
    quality
  });
  if (!blob.size) throw new Error('HEIC 图片转换失败，请换一张图片重试');

  const baseName = file.name.replace(/\.[^.]+$/, '').trim() || 'avatar';
  return new File([blob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now()
  });
}

/**
 * 上传前把手机原图缩放到头像所需尺寸并尽量转成 JPEG。
 * createImageBitmap 在部分 WebView 不稳定，因此保留 HTMLImageElement 解码兜底。
 * 两种原生方式都无法读取时，再按需加载 HEIC 解码器并转成通用 JPEG。
 */
export async function compressImage(file: File, maxSize = 800, quality = 0.8): Promise<File> {
  let decoded: DecodedImage | undefined;
  let sourceFile = file;

  try {
    try {
      decoded = await decodeImage(sourceFile);
    } catch {
      sourceFile = await convertHeicToJpeg(file, quality);
      decoded = await decodeImage(sourceFile);
    }

    const longestSide = Math.max(decoded.width, decoded.height);
    const scale = longestSide > maxSize ? maxSize / longestSide : 1;
    const targetWidth = Math.max(1, Math.round(decoded.width * scale));
    const targetHeight = Math.max(1, Math.round(decoded.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('当前设备无法处理图片，请重试');

    // JPEG 没有透明通道，先铺白底可避免透明 PNG 变成黑色头像。
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, targetWidth, targetHeight);
    context.drawImage(decoded.source, 0, 0, targetWidth, targetHeight);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', quality);
    });
    if (!blob?.size) throw new Error('图片压缩失败，请重试');

    // 常规图片若转码后更大则继续上传原文件；HEIC/非常规扩展仍转为兼容的 JPEG。
    if (blob.size >= file.size && canKeepOriginal(file)) return file;

    const baseName = file.name.replace(/\.[^.]+$/, '').trim() || 'avatar';
    return new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
  } finally {
    decoded?.dispose();
  }
}
